// Shared adminUser-domain i18n keys (used by UserListing, UserAdd, UserDetail, UserProfileEdit).
// Page-level keys live under `UserListing.*`, `UserAdd.*`, `UserDetail.*`, `UserProfileEdit.*`.
// Cross-cutting shared keys (status labels, etc.) live under `adminUser.status.*`.

export const adminUserDict: Record<string, [en: string, th: string]> = {
  // ── Page titles / breadcrumbs ───────────────────────────
  "UserListing.pageTitle": ["Users Management", "จัดการผู้ใช้"],
  "UserDetail.pageTitle": ["User Details", "รายละเอียดผู้ใช้"],

  // ── UserListing — page-level keys ───────────────────────
  "UserListing.search": ["Search Users", "ค้นหาผู้ใช้"],
  "UserListing.filter.allGroups": ["All groups", "ทุกกลุ่ม"],
  "UserListing.filter.noGroup": ["No group", "ไม่มีกลุ่ม"],
  "UserListing.column.email": ["Email", "อีเมล"],
  "UserListing.column.note": ["Note", "หมายเหตุ"],
  "UserListing.column.group": ["Group", "กลุ่ม"],
  "UserListing.column.company": ["Company", "บริษัท"],
  "UserListing.column.status": ["Status", "สถานะ"],
  "UserListing.column.mailSent": ["Mail Sent", "สถานะการส่งอีเมล"],
  "UserListing.column.invitedAt": ["Invited At", "วันที่เชิญ"],
  "UserListing.column.acceptedAt": ["Accepted At", "วันที่ยอมรับคำเชิญ"],
  "UserListing.column.lastLogin": ["Last Login", "เข้าใช้งานล่าสุด"],
  "UserListing.column.createdAt": ["Created At", "วันที่สร้าง"],
  "UserListing.tag.superadmin": ["superadmin", "ผู้ดูแลระบบสูงสุด"],
  "UserListing.mailStatus.sent": ["Sent", "ส่งแล้ว"],
  "UserListing.mailStatus.failed": ["Failed", "ล้มเหลว"],
  "UserListing.mailStatus.sending": ["Sending…", "กำลังส่ง…"],

  // ── UserListing — Import dialog ────────────────────────
  "UserListing.importDialog.title": ["Import Users from Excel", "นำเข้าผู้ใช้จากไฟล์ Excel"],
  "UserListing.import.dropOrBrowse": ["Drop file here or <em>click to browse</em>", "วางไฟล์ที่นี่ หรือ<em>คลิกเพื่อเลือกไฟล์</em>"],
  "UserListing.import.fileTypes": [".xlsx or .xls files only", "รองรับเฉพาะไฟล์ .xlsx หรือ .xls"],
  "UserListing.import.importing": ["Importing users…", "กำลังนำเข้าผู้ใช้…"],
  "UserListing.import.percent": ["{percent}%", "{percent}%"],
  "UserListing.import.complete": ["Import Complete", "นำเข้าเสร็จสิ้น"],
  "UserListing.import.created": ["Created {count}", "สร้างสำเร็จ {count} รายการ"],
  "UserListing.import.skipped": ["Skipped {count}", "ข้าม {count} รายการ"],
  "UserListing.import.errors": ["Errors {count}", "ผิดพลาด {count} รายการ"],

  // ── UserAdd — page-level keys ───────────────────────────
  "UserAdd.modal.add": ["Add User", "เพิ่มผู้ใช้"],
  "UserAdd.modal.edit": ["Edit User", "แก้ไขข้อมูลผู้ใช้"],
  "UserAdd.tab.general": ["General", "ข้อมูลทั่วไป"],
  "UserAdd.tab.permissions": ["Permissions", "สิทธิ์การใช้งาน"],
  "UserAdd.permissionsHint": [
    "Select permissions for this user. These permissions will override the role's permissions.",
    "เลือกสิทธิ์สำหรับผู้ใช้นี้ ระบบจะใช้สิทธิ์เหล่านี้แทนสิทธิ์ที่ได้รับจากบทบาท",
  ],
  "UserAdd.field.avatar": ["Avatar", "รูปโปรไฟล์"],
  "UserAdd.field.firstName": ["First Name", "ชื่อ"],
  "UserAdd.field.lastName": ["Last Name", "นามสกุล"],
  "UserAdd.field.email": ["Email", "อีเมล"],
  "UserAdd.field.note": ["Note", "หมายเหตุ"],
  "UserAdd.field.newPassword": ["New Password", "รหัสผ่านใหม่"],
  "UserAdd.field.confirmPassword": ["Confirm Password", "ยืนยันรหัสผ่านใหม่"],
  "UserAdd.field.userGroup": ["User Group", "กลุ่มผู้ใช้งาน"],
  "UserAdd.field.accessLevel": ["Access Level", "ระดับสิทธิ์"],
  "UserAdd.field.status": ["Status", "สถานะ"],
  "UserAdd.avatar.clickHint": ["Click camera icon to change avatar", "คลิกไอคอนกล้องเพื่อเปลี่ยนรูปโปรไฟล์"],
  "UserAdd.avatar.maxSize": ["Max file size: 5MB", "ขนาดไฟล์สูงสุด 5MB"],
  "UserAdd.avatar.allowed": ["Allowed formats: JPG, PNG, GIF", "รองรับไฟล์ JPG, PNG, GIF"],
  "UserAdd.accessLevel.platformSuperadmin": ["Platform Superadmin", "ผู้ดูแลระบบสูงสุด (Platform)"],
  "UserAdd.accessLevel.platformHint": [
    "Platform access is managed separately from the company user flow.",
    "สิทธิ์ระดับ Platform จัดการแยกต่างหากจากการจัดการผู้ใช้ทั่วไปของบริษัท",
  ],
  "UserAdd.button.save": ["Save", "บันทึก"],
  "UserAdd.button.create": ["Create", "สร้าง"],

  // ── UserDetail — page-level keys ────────────────────────
  "UserDetail.tab.overview": ["Overview", "ภาพรวม"],
  "UserDetail.tab.permissions": ["Permissions", "สิทธิ์การใช้งาน"],
  "UserDetail.tab.activityLog": ["Activity Log", "ประวัติการใช้งาน"],
  "UserDetail.noRole": ["No role", "ยังไม่ได้กำหนดบทบาท"],
  "UserDetail.superadmin": ["Superadmin", "ผู้ดูแลระบบสูงสุด"],
  "UserDetail.button.editUser": ["Edit User", "แก้ไขข้อมูลผู้ใช้"],
  "UserDetail.button.back": ["Back", "กลับ"],
  "UserDetail.card.userStatistics": ["User Statistics", "สถิติผู้ใช้งาน"],
  "UserDetail.card.userStatisticsSubtitle": ["Overview of user activity", "ภาพรวมกิจกรรมการใช้งาน"],
  "UserDetail.card.userInformation": ["User Information", "ข้อมูลผู้ใช้"],
  "UserDetail.card.userInformationSubtitle": ["Personal details and settings", "รายละเอียดส่วนตัวและการตั้งค่า"],
  "UserDetail.card.userPermissions": ["User Permissions", "สิทธิ์ผู้ใช้"],
  "UserDetail.card.userPermissionsSubtitle": ["Effective permissions from user record", "สิทธิ์ที่ใช้งานได้ของผู้ใช้นี้"],
  "UserDetail.card.activityLog": ["Activity Log", "ประวัติการใช้งาน"],
  "UserDetail.card.activityLogSubtitle": ["Recent user activities and system interactions", "กิจกรรมล่าสุดและการใช้งานระบบของผู้ใช้"],
  "UserDetail.stat.lastLogin": ["Last Login", "เข้าใช้งานล่าสุด"],
  "UserDetail.stat.accountCreated": ["Account Created", "วันที่สร้างบัญชี"],
  "UserDetail.stat.totalLogins": ["Total Logins", "จำนวนครั้งที่เข้าใช้งาน"],
  "UserDetail.field.fullName": ["Full Name", "ชื่อ-นามสกุล"],
  "UserDetail.field.email": ["Email", "อีเมล"],
  "UserDetail.field.status": ["Status", "สถานะ"],
  "UserDetail.field.lastLogin": ["Last Login", "เข้าใช้งานล่าสุด"],
  "UserDetail.field.role": ["Role", "บทบาท"],
  "UserDetail.noPermissions": ["No permissions", "ยังไม่มีสิทธิ์ที่กำหนด"],
  "UserDetail.permissionsOther": ["Other", "สิทธิ์อื่น ๆ"],
  "UserDetail.permissionsHeading": ["Permissions", "สิทธิ์"],

  // ── UserDetail — Activity Log filters ──────────────────
  "UserDetail.activity.placeholder.type": ["Activity Type", "ประเภทกิจกรรม"],
  "UserDetail.activity.placeholder.status": ["Status", "สถานะ"],
  "UserDetail.activity.placeholder.startDate": ["Start date", "วันที่เริ่มต้น"],
  "UserDetail.activity.placeholder.endDate": ["End date", "วันที่สิ้นสุด"],
  "UserDetail.activity.typeAll": ["All Types", "ทุกประเภท"],
  "UserDetail.activity.type.login": ["Login", "เข้าสู่ระบบ"],
  "UserDetail.activity.type.logout": ["Logout", "ออกจากระบบ"],
  "UserDetail.activity.type.create": ["Create", "สร้าง"],
  "UserDetail.activity.type.update": ["Update", "แก้ไข"],
  "UserDetail.activity.type.delete": ["Delete", "ลบ"],
  "UserDetail.activity.type.view": ["View", "ดู"],
  "UserDetail.activity.statusAll": ["All Status", "ทุกสถานะ"],
  "UserDetail.activity.status.success": ["Success", "สำเร็จ"],
  "UserDetail.activity.status.failed": ["Failed", "ล้มเหลว"],
  "UserDetail.activity.status.pending": ["Pending", "รอดำเนินการ"],
  "UserDetail.activity.button.refresh": ["Refresh", "รีเฟรช"],
  "UserDetail.activity.empty": ["No activity logs found for this user.", "ยังไม่มีประวัติการใช้งานของผู้ใช้นี้"],
  "UserDetail.activity.loadMore": ["Load More Activities", "โหลดประวัติเพิ่มเติม"],
  "UserDetail.activity.errorPrefix": ["Error: {msg}", "ผิดพลาด: {msg}"],

  // ── UserProfileEdit — page-level keys ──────────────────
  "UserProfileEdit.title": ["Edit Profile", "แก้ไขข้อมูลส่วนตัว"],
  "UserProfileEdit.avatarHint": ["Click camera to change profile image", "คลิกไอคอนกล้องเพื่อเปลี่ยนรูปโปรไฟล์"],
  "UserProfileEdit.field.firstName": ["First Name", "ชื่อ"],
  "UserProfileEdit.field.lastName": ["Last Name", "นามสกุล"],

  // ── Shared user status labels (used by UserListing, UserAdd, UserDetail) ──
  "adminUser.status.active": ["Active", "ใช้งานอยู่"],
  "adminUser.status.pendingInvite": ["Pending Invite", "รอเชิญเข้าใช้งาน"],
  "adminUser.status.inactive": ["Inactive", "ปิดใช้งาน"],
};
